import { useSession } from "@how/app-kit";
import {
  IconAdjustments,
  IconReceipt2,
  IconBug,
  IconBuildingChurch,
  IconHome,
  IconLifebuoy,
  IconMessageQuestion,
  IconPalette,
  IconShieldLock,
  IconWebhook,
  IconWorldWww,
} from "@tabler/icons-react";
import { AppFrame, ShellBrand, type AppFrameNavItem } from "@how/ui";
import { usePlatformRole, type PlatformRole } from "../features/auth/platform-role";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";

interface NavItem extends AppFrameNavItem {
  /** Platform roles allowed to see the entry; navigation is convenience, not authorization. */
  readonly roles: readonly PlatformRole[];
}

const NAV: readonly NavItem[] = [
  { to: "/", label: "Overview", icon: IconHome, roles: ["super_admin", "support"] },
  { to: "/tenants", label: "Tenants", icon: IconBuildingChurch, roles: ["super_admin", "support"] },
  {
    to: "/support",
    label: "Support sessions",
    icon: IconLifebuoy,
    roles: ["super_admin", "support"],
  },
  { to: "/domains", label: "Domains", icon: IconWorldWww, roles: ["super_admin", "support"] },
  { to: "/errors", label: "Errors", icon: IconBug, roles: ["super_admin", "support"] },
  // A super admin's only: the database refuses everyone else (issue #256).
  { to: "/webhooks", label: "Webhook events", icon: IconWebhook, roles: ["super_admin"] },
  // "Was this helpful?" answers from the help center (ADR 0059 amendment, D27): support reads them too.
  {
    to: "/help-feedback",
    label: "Help feedback",
    icon: IconMessageQuestion,
    roles: ["super_admin", "support"],
  },
  { to: "/theme", label: "Theme gallery", icon: IconPalette, roles: ["super_admin"] },
  { to: "/plans", label: "Plans", icon: IconReceipt2, roles: ["super_admin", "support"] },
  { to: "/settings", label: "Platform settings", icon: IconAdjustments, roles: ["super_admin"] },
];

/** The control plane shell: AppFrame with role-gated navigation and the platform role in the account menu. */
export function Shell() {
  const session = useSession();
  const role = usePlatformRole();
  const email = session.status === "signed_in" ? (session.session.user.email ?? "") : "";
  const currentRole = role.data ?? null;
  const items = NAV.filter((item) => currentRole !== null && item.roles.includes(currentRole));
  return (
    <AppFrame
      brand={<ShellBrand surface="Control plane" />}
      nav={items}
      account={{
        email,
        label: currentRole === null ? "no platform role" : currentRole.replace("_", " "),
        // HoW's help center, opened on the articles written for DCX support (ADR 0059).
        helpUrl: `https://${env.VITE_HOW_PLATFORM_BASE_DOMAIN}/help/for/dcx-support`,
        links: [{ to: "/security", label: "Security", icon: IconShieldLock }],
        onSignOut: () => {
          void supabase.auth.signOut();
        },
      }}
    />
  );
}
